import { useToast } from '@packages/chakra-v3';
import { useGuestCartMutations, useMemberCartMutations } from './mutations';
import { useAuthStore } from '@/modules/auth';

type AddCartItemInput = {
	productId: string;
	quantity:  number;
};

type UpdateCartItemInput = {
	cartItemId: string;
	quantity:   number;
};

const useCartMutation = () => {
	const { showSuccess, showError } = useToast();
	const { checkIsAuthenticate } = useAuthStore();

	const isAuthenticated = checkIsAuthenticate();
	const guestCartMutation = useGuestCartMutations();
	const memberCartMutation = useMemberCartMutations();

	const cartMutation = isAuthenticated ? memberCartMutation : guestCartMutation;

	const handleAddCartItem = async({ productId, quantity }: AddCartItemInput) => {
		const addCartItemRequest = {
			body: {
				productId,
				quantity,
			},
		};

		await cartMutation.addCartItemMutation.mutateAsync(addCartItemRequest, {
			onSuccess: () => showSuccess({ title: 'Item added to cart' }),
			onError:   async(error) => {
				const { status } = error as { status: number };

				if (status === 404)  {
					await cartMutation.createCartMutation.mutateAsync({});
					await cartMutation.addCartItemMutation.mutateAsync(addCartItemRequest);
					showSuccess({ title: 'Item added to cart' });
				}
				else {
					showError({ title: 'Failed to add item to cart' });
				}
			},
		});
	};

	const handleUpdateCartItem = async({ cartItemId, quantity }: UpdateCartItemInput) => {
		const updateCartItemRequest = {
			body:   { quantity },
			params: { cartItemId },
		};

		await cartMutation.updateCartItemMutation.mutateAsync(updateCartItemRequest, {
			onSuccess: () => showSuccess({ title: 'Cart Item updated' }),
			onError:   () => showError({ title: 'Failed to update item in cart' }),
		});
	};

	const handleRemoveCartItem = async(cartItemId: string) => {
		const request = { params: { cartItemId } };
		await cartMutation.removeCartItemMutation.mutateAsync(request, {
			onSuccess: () => showSuccess({ title: 'Item removed from cart' }),
			onError:   () => showError({ title: 'Failed to remove item from cart' }),
		});
	};

	const handleClearCart = async() => {
		await cartMutation.clearCartMutation.mutateAsync({}, {
			onSuccess: () => showSuccess({ title: 'Cart cleared' }),
			onError:   () => showError({ title: 'Failed to clear cart' }),
		});
	};

	return {
		handleAddCartItem,
		handleUpdateCartItem,
		handleRemoveCartItem,
		handleClearCart,
	};
};

export default useCartMutation;